import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import NotificationMapper, { NotificationData } from './NotificationMapper';

// 通知容器属性
interface NotificationContainerProps {
  notifications: NotificationData[];
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center';
  maxNotifications?: number;
  autoClose?: boolean;
  autoCloseDelay?: number;
  onNotificationClose?: (notificationId: string) => void;
}

// 通知容器组件
const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  position = 'top-right',
  maxNotifications = 5,
  autoClose = true,
  autoCloseDelay = 5000,
  onNotificationClose,
}) => {
  const [visibleNotifications, setVisibleNotifications] = useState<NotificationData[]>([]);
  const [closingNotifications, setClosingNotifications] = useState<Set<string>>(new Set());

  // 更新可见通知
  useEffect(() => {
    const newNotifications = notifications.slice(-maxNotifications);
    setVisibleNotifications(newNotifications);
  }, [notifications, maxNotifications]);

  // 自动关闭通知
  useEffect(() => {
    if (!autoClose) return;

    const timers: NodeJS.Timeout[] = [];

    visibleNotifications.forEach((notification) => {
      if (!closingNotifications.has(notification.notification_id)) {
        const timer = setTimeout(() => {
          handleNotificationClose(notification.notification_id);
        }, autoCloseDelay);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [visibleNotifications, autoClose, autoCloseDelay, closingNotifications]);

  // 处理通知关闭
  const handleNotificationClose = (notificationId: string) => {
    setClosingNotifications(prev => new Set(prev).add(notificationId));
    
    // 延迟移除通知以允许退出动画播放
    setTimeout(() => {
      setVisibleNotifications(prev => 
        prev.filter(n => n.notification_id !== notificationId)
      );
      setClosingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
      onNotificationClose?.(notificationId);
    }, 300);
  };

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <StyledContainer position={position}>
      {visibleNotifications.map((notification) => (
        <NotificationWrapper
          key={notification.notification_id}
          isClosing={closingNotifications.has(notification.notification_id)}
        >
          <NotificationMapper
            notification={notification}
            onClose={handleNotificationClose}
          />
        </NotificationWrapper>
      ))}
    </StyledContainer>
  );
};

// 退出动画
const slideOut = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.8);
  }
`;

// 通知包装器
const NotificationWrapper = styled.div<{ isClosing: boolean }>`
  animation: ${props => props.isClosing ? slideOut : 'none'} 0.3s ease-in-out;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// 容器样式
const StyledContainer = styled.div<{ position: string }>`
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  
  ${props => {
    switch (props.position) {
      case 'top-right':
        return `
          top: 20px;
          right: 20px;
        `;
      case 'top-left':
        return `
          top: 20px;
          left: 20px;
        `;
      case 'bottom-right':
        return `
          bottom: 20px;
          right: 20px;
        `;
      case 'bottom-left':
        return `
          bottom: 20px;
          left: 20px;
        `;
      case 'top-center':
        return `
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
        `;
      default:
        return `
          top: 20px;
          right: 20px;
        `;
    }
  }}
  
  > * {
    pointer-events: auto;
  }
  
  @media (max-width: 2768px) {
    left: 10px;
    right: 10px;
    transform: none;
    
    ${props => props.position === 'top-center' ? `
      left: 10px;
      right: 10px;
      transform: none;
    ` : ''}
  }
`;

export default NotificationContainer;